import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, RefreshCw, CheckCircle2, XCircle, Clock, Send, Loader2,
  Search, Filter, AlertTriangle, BarChart3, Inbox
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = {
  drip_day3: "Drip Dia 3",
  drip_day6: "Drip Dia 6",
  rsvp_confirmation: "Convite RSVP",
  save_the_date: "Save the Date",
  qrcode_access: "QR Code",
  rsvp_reminder: "Lembrete RSVP",
  custom_alert: "Alerta custom",
  password_reset: "Reset de senha",
  test: "Teste",
};

const STATUS_CFG = {
  sent: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  failed: { cls: "bg-rose-50 text-rose-600 border-rose-200", icon: XCircle },
  pending: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  const Ico = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-bold uppercase tracking-wider border ${cfg.cls}`}>
      <Ico size={10} strokeWidth={2.5} />
      {status || "—"}
    </span>
  );
}

export default function EmailLogsPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterKind, setFilterKind] = useState("all");
  const [showForceSend, setShowForceSend] = useState(false);

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-logs?limit=200`, { headers: authHeaders });
      setItems(res.data.items || []);
      setStats(res.data.stats_30d || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao carregar logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  const handleTick = async () => {
    try {
      const res = await axios.post(`${API}/admin/drip/tick`, {}, { headers: authHeaders });
      const s = res.data.summary;
      toast.success(`Tick rodou. Dia3: ${s.day3.sent}/${s.day3.found} · Dia6: ${s.day6.sent}/${s.day6.found}`);
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no tick");
    }
  };

  // counters
  const sentCount = items.filter(i => i.status === "sent").length;
  const failedCount = items.filter(i => i.status === "failed").length;
  const dripDay3 = stats.drip_day3?.sent || 0;
  const dripDay6 = stats.drip_day6?.sent || 0;

  const filtered = items.filter((it) => {
    if (filterKind !== "all" && it.kind !== filterKind) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (it.user_email || "").toLowerCase().includes(q) ||
      (it.subject || "").toLowerCase().includes(q) ||
      (it.kind || "").toLowerCase().includes(q)
    );
  });

  const kindOptions = ["all", ...Array.from(new Set(items.map(i => i.kind).filter(Boolean)))];

  return (
    <div className="space-y-6" style={{ fontFamily: "'Poppins', sans-serif" }} data-testid="email-logs-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 mb-1">
            <Mail size={18} className="text-violet-500" />
            <span className="text-[11px] font-extrabold text-violet-700 uppercase tracking-widest">Email Audit</span>
          </div>
          <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Logs de Email</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Histórico de envios, status, falhas e drip campaigns</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchLogs} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-all" data-testid="email-refresh-btn">
            <RefreshCw size={14} />
            Atualizar
          </button>
          <button onClick={handleTick} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm bg-amber-100 text-amber-700 hover:bg-amber-200 transition-all font-medium" data-testid="email-tick-btn" title="Roda o scheduler manualmente (procura usuários elegíveis)">
            <Send size={14} />
            Tick Drip
          </button>
          <button onClick={() => setShowForceSend(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-[0_4px_14px_-4px_rgba(139,92,246,0.5)] hover:shadow-[0_6px_20px_-4px_rgba(139,92,246,0.7)] transition-all font-medium" data-testid="email-force-send-btn">
            <Mail size={14} />
            Forçar envio
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="email-stats">
        <StatCard label="Enviados" value={sentCount} color="text-emerald-600" bg="bg-emerald-50 border-emerald-100" />
        <StatCard label="Falhas" value={failedCount} color="text-rose-500" bg="bg-rose-50 border-rose-100" />
        <StatCard label="Drip Dia 3 (30d)" value={dripDay3} color="text-violet-600" bg="bg-violet-50 border-violet-100" />
        <StatCard label="Drip Dia 6 (30d)" value={dripDay6} color="text-fuchsia-600" bg="bg-fuchsia-50 border-fuchsia-100" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por email, assunto ou tipo…"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-white border border-zinc-200 text-[13px] focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
            data-testid="email-search"
          />
        </div>
        <select
          value={filterKind}
          onChange={(e) => setFilterKind(e.target.value)}
          className="h-10 px-3 rounded-lg bg-white border border-zinc-200 text-[13px] focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
          data-testid="email-filter-kind"
        >
          {kindOptions.map(k => (
            <option key={k} value={k}>{k === "all" ? "Todos os tipos" : (KIND_LABEL[k] || k)}</option>
          ))}
        </select>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center" data-testid="email-loading">
            <Loader2 size={28} className="animate-spin mx-auto text-violet-500" />
            <p className="text-[13px] text-zinc-500 mt-3">Carregando logs…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="email-empty">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-zinc-100 flex items-center justify-center mb-4">
              <Inbox size={24} className="text-zinc-400" />
            </div>
            <p className="text-[14px] font-semibold text-zinc-700">Nenhum email registrado {search || filterKind !== "all" ? "com esses filtros" : "ainda"}</p>
            <p className="text-[12.5px] text-zinc-500 mt-1">Use "Forçar envio" pra testar o sistema</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filtered.map((log) => (
              <div key={log.id} className="p-4 hover:bg-zinc-50 transition-colors" data-testid={`email-log-row-${log.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${log.status === "sent" ? "bg-emerald-50 text-emerald-600" : log.status === "failed" ? "bg-rose-50 text-rose-500" : "bg-amber-50 text-amber-600"}`}>
                    <Mail size={15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="text-[13.5px] font-semibold text-zinc-900 truncate">{KIND_LABEL[log.kind] || log.kind || "—"}</span>
                      <StatusBadge status={log.status} />
                      {log.provider && <span className="text-[10.5px] text-zinc-400 font-mono uppercase">{log.provider}</span>}
                    </div>
                    <p className="text-[12.5px] text-zinc-600 truncate">→ {log.user_email || "—"}</p>
                    {log.subject && <p className="text-[12px] text-zinc-500 italic truncate mt-0.5">"{log.subject}"</p>}
                    {log.error && (
                      <div className="mt-1.5 px-2.5 py-1.5 bg-rose-50 border border-rose-100 rounded-md inline-flex items-start gap-1.5 max-w-full">
                        <AlertTriangle size={11} className="text-rose-500 shrink-0 mt-0.5" />
                        <p className="text-[11px] text-rose-700 break-all">{log.error}</p>
                      </div>
                    )}
                  </div>
                  <span className="text-[11px] text-zinc-400 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForceSend && (
        <ForceSendModal
          onClose={() => setShowForceSend(false)}
          onSent={() => { setShowForceSend(false); fetchLogs(); }}
          authHeaders={authHeaders}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, color, bg }) {
  return (
    <div className={`rounded-2xl border p-4 ${bg}`}>
      <p className="text-[10.5px] font-bold uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`text-[26px] font-bold tracking-tighter tabular-nums leading-tight ${color} mt-0.5`}>{value}</p>
    </div>
  );
}

function ForceSendModal({ onClose, onSent, authHeaders }) {
  const [email, setEmail] = useState("");
  const [day, setDay] = useState(3);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { toast.error("Email inválido"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/drip/force-send`, { email: email.trim().toLowerCase(), day }, { headers: authHeaders });
      if (res.data.ok) {
        toast.success(`Drip Dia ${day} enviado para ${email}`);
        onSent();
      } else {
        toast.error(`Falha: ${res.data.result?.error || "erro desconhecido"}`);
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao enviar");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="force-send-modal">
      <div className="bg-white w-full sm:max-w-[440px] rounded-t-3xl sm:rounded-3xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-zinc-100">
          <h2 className="text-[17px] font-semibold text-zinc-900 flex items-center gap-2">
            <Send size={16} className="text-violet-500" />
            Forçar envio de drip
          </h2>
          <p className="text-[12px] text-zinc-500 mt-0.5">Re-envia drip pra um email específico (bypassa idempotência)</p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3.5">
          <div>
            <label className="text-[11.5px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Email do usuário</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="usuario@exemplo.com" className="w-full h-11 px-3 rounded-lg bg-white border border-zinc-200 text-[13.5px] focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100" data-testid="force-send-email" />
          </div>
          <div>
            <label className="text-[11.5px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Qual drip enviar</label>
            <div className="grid grid-cols-2 gap-2">
              {[3, 6].map(d => (
                <button key={d} type="button" onClick={() => setDay(d)} className={`h-11 rounded-lg text-[13px] font-medium transition-all ${day === d ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`} data-testid={`force-send-day-${d}`}>
                  Dia {d} {d === 3 ? "(humano)" : "(urgência)"}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-11 rounded-lg bg-zinc-100 text-zinc-700 text-[13px] font-medium hover:bg-zinc-200">Cancelar</button>
            <button type="submit" disabled={submitting} className="flex-1 h-11 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white text-[13px] font-bold flex items-center justify-center gap-1.5 shadow-[0_4px_14px_-4px_rgba(139,92,246,0.5)] disabled:opacity-60" data-testid="force-send-submit">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Enviar agora
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
